import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { useOutlets } from '@/hooks/useOutlets';
import type { AuditLogEntry } from '@photobooth/api-client';

interface AuditLogViewerProps {
  userId: string;
}

export function AuditLogViewer({ userId }: AuditLogViewerProps) {
  const { outlets } = useOutlets();
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filter states
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedOutletId, setSelectedOutletId] = useState('');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    fetchAuditLogs();
  }, [userId, startDate, endDate, selectedOutletId, currentPage]);

  const fetchAuditLogs = async () => {
    setIsLoading(true);
    setError('');

    try {
      const params: {
        startDate?: string;
        endDate?: string;
        outletId?: string;
        limit?: number;
        offset?: number;
      } = {
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage,
      };

      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (selectedOutletId) params.outletId = selectedOutletId;

      const response = await api.cashiers.getAuditLogs(userId, params);
      
      if (response.success && response.data) {
        const raw = response.data as any;
        // Support both direct array and wrapped { data: AuditLogEntry[] } shapes
        const logsData = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
        setLogs(logsData);
      } else {
        setError(response.error?.message || 'Failed to load audit logs');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit logs');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setSelectedOutletId('');
    setCurrentPage(1);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatAction = (action: string) => {
    return action
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getOutletName = (outletId?: string) => {
    if (!outletId) return 'N/A';
    const outlet = outlets.find(o => o.outletId === outletId);
    return outlet ? outlet.name : outletId;
  };

  const formatMetadata = (metadata?: Record<string, unknown>) => {
    if (!metadata) return 'N/A';
    
    // Format common metadata fields
    const entries = Object.entries(metadata);
    if (entries.length === 0) return 'N/A';
    
    return entries
      .map(([key, value]) => {
        if (typeof value === 'object' && value !== null) {
          return `${key}: ${JSON.stringify(value)}`;
        }
        return `${key}: ${value}`;
      })
      .join(', ');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-text">Audit Logs</h2>
      </div>

      {/* Filters */}
      <div className="bg-surface-light p-4 rounded-lg space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-text mb-1">
              Start Date
            </label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div>
            <label htmlFor="endDate" className="block text-sm font-medium text-text mb-1">
              End Date
            </label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div>
            <label htmlFor="outlet" className="block text-sm font-medium text-text mb-1">
              Outlet
            </label>
            <select
              id="outlet"
              value={selectedOutletId}
              onChange={(e) => {
                setSelectedOutletId(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Outlets</option>
              {outlets.map((outlet) => (
                <option key={outlet.outletId} value={outlet.outletId}>
                  {outlet.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            onClick={handleClearFilters}
            className="px-4 py-2 text-sm text-primary hover:text-primary-dark"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      )}

      {/* Audit logs table */}
      {!isLoading && !error && (
        <>
          <div className="bg-surface rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-border">
                <thead className="bg-surface-light">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                      Timestamp
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                      Action
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                      Entity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                      Outlet
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-surface divide-y divide-border">
                  {logs.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-4 text-center text-text-secondary">
                        No audit logs found
                      </td>
                    </tr>
                  ) : (
                    logs.map((log) => (
                      <tr key={log.id} className="hover:bg-surface-light">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-text">
                          {formatDate(log.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-text">
                          {formatAction(log.action)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-text">
                          <div>
                            <div className="font-medium">{log.entityType}</div>
                            <div className="text-text-secondary text-xs">{log.entityId}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-text">
                          {getOutletName(log.outletId)}
                        </td>
                        <td className="px-6 py-4 text-sm text-text">
                          <div className="max-w-xs truncate" title={formatMetadata(log.metadata)}>
                            {formatMetadata(log.metadata)}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {logs.length > 0 && (
            <div className="flex items-center justify-between px-4 py-3 bg-surface rounded-lg shadow">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm border border-border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-surface-light"
                >
                  Previous
                </button>
                <span className="text-sm text-text">
                  Page {currentPage}
                </span>
                <button
                  onClick={() => setCurrentPage((prev) => prev + 1)}
                  disabled={logs.length < itemsPerPage}
                  className="px-3 py-1 text-sm border border-border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-surface-light"
                >
                  Next
                </button>
              </div>
              <div className="text-sm text-text-secondary">
                Showing {logs.length} entries
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
